import React from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { 
  Package, ShoppingBag, TrendingUp, Star, Gift, Tag, Heart, Clock,
  FileText, Calculator, Send, CheckCircle, AlertCircle, FileSpreadsheet,
  ShoppingCart, CreditCard, Truck, RotateCcw, MapPin, Calendar,
  HelpCircle, MessageSquare, Phone, Mail, BookOpen, FileQuestion,
  Users, Building, Target, Award, Briefcase, Globe,
  BookMarked, Settings, Shield, Cpu, Database, Code,
  BarChart3, Package2, Bell, DollarSign, Grid, UserCheck, Zap, X
} from 'lucide-react';
import { media } from '../../styles/responsive';

/**
 * 넓은 화면에서는 본문 옆의 한 칸, 좁은 화면에서는 왼쪽에서 미끄러져 나오는
 * 서랍이다.
 *
 * 예전에는 어느 폭에서나 position:absolute 로 떠 있었고 본문이 margin-left
 * 250px 으로 피했다. 그 margin 은 1200px 이하에서 0 이 되는데 사이드바는
 * 그대로 떠 있어서, 폴드를 편 폭(1200px 언저리)에서 본문이 사이드바 아래로
 * 들어가 메뉴 글자가 상품 카드에 덮였다. 그래서 흐름 안으로 들여놓았다.
 */
const SidebarContainer = styled.aside`
  flex: 0 0 var(--market-sidebar-w);
  width: var(--market-sidebar-w);
  align-self: flex-start;
  /* 본문이 길어도 메뉴는 따라 붙어 있게. 헤더가 fixed 라 그 높이만큼 내린다. */
  position: sticky;
  top: var(--market-header-h);
  max-height: calc(100vh - var(--market-header-h));
  padding: 30px 20px;
  background: #fafafa;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
  overscroll-behavior: contain;

  ${media.lg} {
    position: fixed;
    top: var(--market-header-h);
    left: 0;
    /* 높이를 직접 준다. top + bottom 으로 맡겨 두면 메뉴가 화면보다 길 때
     * 크롬이 내용 높이(1200px 넘게)를 그대로 쓰면서 top 까지 흘려버려,
     * 서랍이 화면 맨 위부터 그려지며 헤더를 덮었다.
     * dvh 는 폰에서 주소창이 접힐 때의 실제 높이다. */
    height: calc(100vh - var(--market-header-h));
    height: calc(100dvh - var(--market-header-h));
    /* 폭을 화면에 양보한다. 좁은 폰에서 280px 을 고집하면 본문이 손톱만큼만
     * 남아 서랍이 열린 건지 화면이 깨진 건지 구분이 안 된다. */
    width: min(300px, 84vw);
    flex-basis: auto;
    max-height: none;
    z-index: 1100;
    transform: translateX(${(props) => (props.open ? '0' : '-100%')});
    transition: transform 0.25s ease;
    box-shadow: ${(props) => (props.open ? '0 0 32px rgba(15,23,42,0.18)' : 'none')};
    /* 닫혀 있을 때는 화면 밖에 있으므로 키보드 이동 대상에서도 빼야 한다. */
    visibility: ${(props) => (props.open ? 'visible' : 'hidden')};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** 서랍일 때만 나오는 닫기 줄. 넓은 화면에는 닫을 이유가 없다. */
const DrawerHead = styled.div`
  display: none;

  ${media.lg} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: -10px 0 14px;
  }
`;

const DrawerTitle = styled.strong`
  font-size: 15px;
  font-weight: 800;
  color: #111827;
`;

const DrawerClose = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* 손가락으로 누르는 것이라 44px 아래로 내리지 않는다. */
  width: 44px;
  height: 44px;
  margin-right: -10px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #374151;
  cursor: pointer;

  &:hover { background: #f3f4f6; }
`;

const SidebarTitle = styled.strong`
  display: block;
  margin-bottom: 12px;
  font-size: 14px;
  color: #111;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 30px;
`;


const MenuLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  /* 손가락 대상. 12px 패딩이면 줄 높이가 40px 남짓이라 옆 줄을 같이 누른다. */
  min-height: 44px;
  padding: 10px 14px;
  line-height: 1.35;
  border: none;
  background: ${props => props.active ? '#111827' : 'transparent'};
  color: ${props => props.active ? '#fff' : '#374151'};
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  border-radius: 8px;
  transition: all 0.2s ease;
  font-weight: ${props => props.active ? '600' : '500'};
  width: 100%;
  margin-bottom: 4px;
  text-decoration: none;

  &:hover {
    background-color: ${props => props.active ? '#1f2937' : '#f3f4f6'};
    color: ${props => props.active ? '#fff' : '#111827'};
    transform: translateX(2px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 10px 14px;
  line-height: 1.35;
  border: none;
  background: ${props => props.active ? '#111827' : 'transparent'};
  color: ${props => props.active ? '#fff' : '#374151'};
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  border-radius: 8px;
  transition: all 0.2s ease;
  font-weight: ${props => props.active ? '600' : '500'};
  width: 100%;
  margin-bottom: 4px;
  text-decoration: none;

  &:hover {
    background-color: ${props => props.active ? '#1f2937' : '#f3f4f6'};
    color: ${props => props.active ? '#fff' : '#111827'};
    transform: translateX(2px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
  }
`;

const MenuIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
`;

const MenuText = styled.span`
  flex: 1;
  min-width: 0;
  /* 좁은 서랍에서 "시스템가이드" 같은 말이 잘리지 않게. 넘치면 줄을 바꾼다. */
  overflow-wrap: anywhere;
`;

const Badge = styled.span`
  background: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 16px;
  text-align: center;
`;

const Divider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 20px 0;
`;

const sidebarMenus = {
  // 홈페이지 메뉴 (테마별 쇼핑)
  '/': {
    title: '쇼핑 테마',
    sections: [
      {
        title: '메뉴',
        items: [
          { path: '/ShowItem', label: '전체상품', icon: <Package size={16} /> },
          { path: '/delivery-tracking', label: '배송추적', icon: <Truck size={16} /> },
          { path: '/quote-request', label: '견적의뢰', icon: <FileText size={16} /> },
          { path: '/order-history', label: '주문내역', icon: <ShoppingCart size={16} /> },
          { path: '/mypage', label: '마이페이지', icon: <UserCheck size={16} /> },
          { path: '/support', label: '고객센터', icon: <HelpCircle size={16} /> },
          { path: '/about', label: '회사소개', icon: <Building size={16} /> },
          { path: '/system-guide', label: '시스템가이드', icon: <BookMarked size={16} /> },
          { path: '/point-charge', label: '포인트충전', icon: <DollarSign size={16} /> }
        ]
      },
      {
        title: '테마별 상품',
        items: [
          { path: '/ShowItem/best', label: '베스트 상품', icon: <Award size={16} /> },
          { path: '/ShowItem/new', label: '신상품', icon: <Star size={16} /> },
          { path: '/ShowItem/sale', label: '특가 세일', icon: <Zap size={16} /> },
          { path: '/ShowItem/luxury', label: '럭셔리 컬렉션', icon: <Gift size={16} /> }
        ]
      },
      {
        title: '인기 브랜드',
        items: [
          { label: 'NIKE', icon: <Tag size={16} /> },
          { label: 'ADIDAS', icon: <Tag size={16} /> },
          { label: 'APPLE', icon: <Tag size={16} /> },
          { label: 'SAMSUNG', icon: <Tag size={16} /> }
        ]
      }
    ]
  },
  
  // 전체상품 메뉴 (ShowItem 섹션 내에서만 이동)
  '/ShowItem': {
    title: '상품 카테고리',
    sections: [
      {
        title: '카테고리',
        items: [
          { path: '/ShowItem', label: '전체 상품', icon: <Package size={18} /> },
          { path: '/ShowItem/new', label: '신상품', icon: <Star size={18} />, badge: 'NEW' },
          { path: '/ShowItem/best', label: '베스트셀러', icon: <TrendingUp size={18} /> },
          { path: '/ShowItem/sale', label: '특가/할인', icon: <Tag size={18} />, badge: '~50%' },
          { path: '/ShowItem/gift', label: '선물세트', icon: <Gift size={18} /> },
          { path: '/ShowItem/fashion', label: '패션', icon: <ShoppingBag size={18} /> },
          { path: '/ShowItem/electronics', label: '전자제품', icon: <Cpu size={18} /> },
          { path: '/ShowItem/living', label: '리빙', icon: <Heart size={18} /> }
        ]
      },
      {
        title: '전체상품 카테고리',
        items: [
          { category: '전체', label: '전체상품' },
          { category: '의류', label: '의류' },
          { category: '신발', label: '신발' },
          { category: '가방', label: '가방' },
          { category: '전자제품', label: '전자제품' },
          { category: '뷰티', label: '뷰티' },
          { category: '리빙', label: '리빙' }
        ]
      }
    ]
  },
  
  // 신상품 전용 메뉴
  '/ShowItem/new': {
    title: '상품 카테고리',
    sections: [
      {
        title: '카테고리',
        items: [
          { path: '/ShowItem', label: '전체 상품', icon: <Package size={18} /> },
          { path: '/ShowItem/new', label: '신상품', icon: <Star size={18} />, badge: 'NEW' },
          { path: '/ShowItem/best', label: '베스트셀러', icon: <TrendingUp size={18} /> },
          { path: '/ShowItem/sale', label: '특가/할인', icon: <Tag size={18} />, badge: '~50%' },
          { path: '/ShowItem/gift', label: '선물세트', icon: <Gift size={18} /> },
          { path: '/ShowItem/fashion', label: '패션', icon: <ShoppingBag size={18} /> },
          { path: '/ShowItem/electronics', label: '전자제품', icon: <Cpu size={18} /> },
          { path: '/ShowItem/living', label: '리빙', icon: <Heart size={18} /> }
        ]
      },
      {
        title: '신상품 필터',
        items: [
          { category: '전체', label: '전체 신상품' },
          { category: '의류', label: '신상 의류' },
          { category: '신발', label: '신상 신발' },
          { category: '가방', label: '신상 가방' },
          { category: '전자제품', label: '신상 전자제품' },
          { category: '뷰티', label: '신상 뷰티' },
          { category: '리빙', label: '신상 리빙' }
        ]
      }
    ]
  },
  
  // 베스트셀러 전용 메뉴
  '/ShowItem/best': {
    title: '상품 카테고리',
    sections: [
      {
        title: '카테고리',
        items: [
          { path: '/ShowItem', label: '전체 상품', icon: <Package size={18} /> },
          { path: '/ShowItem/new', label: '신상품', icon: <Star size={18} />, badge: 'NEW' },
          { path: '/ShowItem/best', label: '베스트셀러', icon: <TrendingUp size={18} /> },
          { path: '/ShowItem/sale', label: '특가/할인', icon: <Tag size={18} />, badge: '~50%' },
          { path: '/ShowItem/gift', label: '선물세트', icon: <Gift size={18} /> },
          { path: '/ShowItem/fashion', label: '패션', icon: <ShoppingBag size={18} /> },
          { path: '/ShowItem/electronics', label: '전자제품', icon: <Cpu size={18} /> },
          { path: '/ShowItem/living', label: '리빙', icon: <Heart size={18} /> }
        ]
      },
      {
        title: '베스트셀러 필터',
        items: [
          { category: '전체', label: '전체 베스트' },
          { category: '의류', label: '베스트 의류' },
          { category: '신발', label: '베스트 신발' },
          { category: '가방', label: '베스트 가방' },
          { category: '전자제품', label: '베스트 전자제품' },
          { category: '뷰티', label: '베스트 뷰티' },
          { category: '리빙', label: '베스트 리빙' }
        ]
      }
    ]
  },
  
  // 특가/할인 전용 메뉴
  '/ShowItem/sale': {
    title: '상품 카테고리',
    sections: [
      {
        title: '카테고리',
        items: [
          { path: '/ShowItem', label: '전체 상품', icon: <Package size={18} /> },
          { path: '/ShowItem/new', label: '신상품', icon: <Star size={18} />, badge: 'NEW' },
          { path: '/ShowItem/best', label: '베스트셀러', icon: <TrendingUp size={18} /> },
          { path: '/ShowItem/sale', label: '특가/할인', icon: <Tag size={18} />, badge: '~50%' },
          { path: '/ShowItem/gift', label: '선물세트', icon: <Gift size={18} /> },
          { path: '/ShowItem/fashion', label: '패션', icon: <ShoppingBag size={18} /> },
          { path: '/ShowItem/electronics', label: '전자제품', icon: <Cpu size={18} /> },
          { path: '/ShowItem/living', label: '리빙', icon: <Heart size={18} /> }
        ]
      },
      {
        title: '특가상품 필터',
        items: [
          { category: '전체', label: '전체 특가' },
          { category: '의류', label: '할인 의류' },
          { category: '신발', label: '할인 신발' },
          { category: '가방', label: '할인 가방' },
          { category: '전자제품', label: '할인 전자제품' },
          { category: '뷰티', label: '할인 뷰티' },
          { category: '리빙', label: '할인 리빙' }
        ]
      }
    ]
  },
  
  // 선물세트 전용 메뉴
  '/ShowItem/gift': {
    title: '상품 카테고리',
    sections: [
      {
        title: '카테고리',
        items: [
          { path: '/ShowItem', label: '전체 상품', icon: <Package size={18} /> },
          { path: '/ShowItem/new', label: '신상품', icon: <Star size={18} />, badge: 'NEW' },
          { path: '/ShowItem/best', label: '베스트셀러', icon: <TrendingUp size={18} /> },
          { path: '/ShowItem/sale', label: '특가/할인', icon: <Tag size={18} />, badge: '~50%' },
          { path: '/ShowItem/gift', label: '선물세트', icon: <Gift size={18} /> },
          { path: '/ShowItem/fashion', label: '패션', icon: <ShoppingBag size={18} /> },
          { path: '/ShowItem/electronics', label: '전자제품', icon: <Cpu size={18} /> },
          { path: '/ShowItem/living', label: '리빙', icon: <Heart size={18} /> }
        ]
      },
      {
        title: '선물세트 필터',
        items: [
          { category: '전체', label: '전체 선물세트' },
          { category: '의류', label: '의류 선물세트' },
          { category: '신발', label: '신발 선물세트' },
          { category: '가방', label: '가방 선물세트' },
          { category: '전자제품', label: '전자제품 선물세트' },
          { category: '뷰티', label: '뷰티 선물세트' },
          { category: '리빙', label: '리빙 선물세트' }
        ]
      }
    ]
  },
  
  // 패션 전용 메뉴
  '/ShowItem/fashion': {
    title: '상품 카테고리',
    sections: [
      {
        title: '카테고리',
        items: [
          { path: '/ShowItem', label: '전체 상품', icon: <Package size={18} /> },
          { path: '/ShowItem/new', label: '신상품', icon: <Star size={18} />, badge: 'NEW' },
          { path: '/ShowItem/best', label: '베스트셀러', icon: <TrendingUp size={18} /> },
          { path: '/ShowItem/sale', label: '특가/할인', icon: <Tag size={18} />, badge: '~50%' },
          { path: '/ShowItem/gift', label: '선물세트', icon: <Gift size={18} /> },
          { path: '/ShowItem/fashion', label: '패션', icon: <ShoppingBag size={18} /> },
          { path: '/ShowItem/electronics', label: '전자제품', icon: <Cpu size={18} /> },
          { path: '/ShowItem/living', label: '리빙', icon: <Heart size={18} /> }
        ]
      },
      {
        title: '패션 필터',
        items: [
          { category: '전체', label: '전체 패션' },
          { category: '의류', label: '패션 의류' },
          { category: '신발', label: '패션 신발' },
          { category: '가방', label: '패션 가방' }
        ]
      }
    ]
  },
  
  // 전자제품 전용 메뉴
  '/ShowItem/electronics': {
    title: '상품 카테고리',
    sections: [
      {
        title: '카테고리',
        items: [
          { path: '/ShowItem', label: '전체 상품', icon: <Package size={18} /> },
          { path: '/ShowItem/new', label: '신상품', icon: <Star size={18} />, badge: 'NEW' },
          { path: '/ShowItem/best', label: '베스트셀러', icon: <TrendingUp size={18} /> },
          { path: '/ShowItem/sale', label: '특가/할인', icon: <Tag size={18} />, badge: '~50%' },
          { path: '/ShowItem/gift', label: '선물세트', icon: <Gift size={18} /> },
          { path: '/ShowItem/fashion', label: '패션', icon: <ShoppingBag size={18} /> },
          { path: '/ShowItem/electronics', label: '전자제품', icon: <Cpu size={18} /> },
          { path: '/ShowItem/living', label: '리빙', icon: <Heart size={18} /> }
        ]
      },
      {
        title: '전자제품 필터',
        items: [
          { category: '전체', label: '전체 전자제품' },
          { category: '전자제품', label: '모든 전자제품' },
          { category: '리빙', label: '스마트홈 기기' }
        ]
      }
    ]
  },
  
  // 리빙 전용 메뉴
  '/ShowItem/living': {
    title: '상품 카테고리',
    sections: [
      {
        title: '카테고리',
        items: [
          { path: '/ShowItem', label: '전체 상품', icon: <Package size={18} /> },
          { path: '/ShowItem/new', label: '신상품', icon: <Star size={18} />, badge: 'NEW' },
          { path: '/ShowItem/best', label: '베스트셀러', icon: <TrendingUp size={18} /> },
          { path: '/ShowItem/sale', label: '특가/할인', icon: <Tag size={18} />, badge: '~50%' },
          { path: '/ShowItem/gift', label: '선물세트', icon: <Gift size={18} /> },
          { path: '/ShowItem/fashion', label: '패션', icon: <ShoppingBag size={18} /> },
          { path: '/ShowItem/electronics', label: '전자제품', icon: <Cpu size={18} /> },
          { path: '/ShowItem/living', label: '리빙', icon: <Heart size={18} /> }
        ]
      },
      {
        title: '리빙 필터',
        items: [
          { category: '전체', label: '전체 리빙' },
          { category: '리빙', label: '생활용품' },
          { category: '전자제품', label: '가전제품' }
        ]
      }
    ]
  },
  
  // 견적의뢰 메뉴 (견적 섹션 내에서만 이동)
  '/quote-request': {
    title: '견적 서비스',
    items: [
      { path: '/quote-request', label: '견적 요청하기', icon: <FileText size={18} /> },
      { path: '/quote-request/calculator', label: '견적 계산기', icon: <Calculator size={18} /> },
      { path: '/quote-request/my-quotes', label: '내 견적 목록', icon: <FileSpreadsheet size={18} /> },
      { path: '/quote-request/in-progress', label: '진행중인 견적', icon: <Send size={18} />, badge: '3' },
      { path: '/quote-request/completed', label: '완료된 견적', icon: <CheckCircle size={18} /> },
      { path: '/quote-request/templates', label: '견적 템플릿', icon: <FileText size={18} /> }
    ]
  },
  
  // 배송추적 메뉴 (배송 섹션 내에서만 이동)
  '/delivery-tracking': {
    title: '배송 서비스',
    items: [
      { path: '/delivery-tracking', label: '배송조회', icon: <Truck size={18} /> },
      { path: '/delivery-tracking/status', label: '실시간 추적', icon: <MapPin size={18} />, badge: 'NEW' },
      { path: '/delivery-tracking/history', label: '배송 내역', icon: <Clock size={18} /> },
      { path: '/delivery-tracking/schedule', label: '배송 예정', icon: <Calendar size={18} /> },
      { path: '/delivery-tracking/returns', label: '반품/교환 추적', icon: <RotateCcw size={18} /> },
      { path: '/delivery-tracking/guide', label: '배송 안내', icon: <BookOpen size={18} /> }
    ]
  },

  // 주문내역/마이페이지 메뉴 (마이페이지 섹션 내에서만 이동)
  '/order-history': {
    title: '마이페이지',
    items: [
      { path: '/order-history', label: '주문내역', icon: <ShoppingCart size={18} /> },
      { path: '/mypage', label: '내정보', icon: <UserCheck size={18} /> },
      { path: '/mypage/returns', label: '반품/교환', icon: <RotateCcw size={18} /> },
      { path: '/point-charge', label: '포인트충전', icon: <DollarSign size={18} /> },
      { path: '/mypage/coupons', label: '쿠폰함', icon: <Tag size={18} /> },
      { path: '/mypage/wishlist', label: '찜한 상품', icon: <Heart size={18} /> }
    ]
  },

  '/mypage': {
    title: '마이페이지', 
    items: [
      { path: '/order-history', label: '주문내역', icon: <ShoppingCart size={18} /> },
      { path: '/mypage', label: '내정보', icon: <UserCheck size={18} /> },
      { path: '/mypage/returns', label: '반품/교환', icon: <RotateCcw size={18} /> },
      { path: '/point-charge', label: '포인트충전', icon: <DollarSign size={18} /> },
      { path: '/mypage/coupons', label: '쿠폰함', icon: <Tag size={18} /> },
      { path: '/mypage/wishlist', label: '찜한 상품', icon: <Heart size={18} /> }
    ]
  },
  
  // 고객센터 메뉴 (고객지원 섹션 내에서만 이동)
  '/support': {
    title: '고객지원',
    items: [
      { path: '/support', label: '고객센터 홈', icon: <HelpCircle size={18} /> },
      { path: '/support/faq', label: '자주 묻는 질문', icon: <FileQuestion size={18} /> },
      { path: '/support/inquiry', label: '1:1 문의', icon: <MessageSquare size={18} /> },
      { path: '/support/notice', label: '공지사항', icon: <Bell size={18} /> },
      { path: '/support/call', label: '전화 상담', icon: <Phone size={18} /> },
      { path: '/support/guide', label: '이용 가이드', icon: <BookOpen size={18} /> }
    ]
  },
  
  // 회사소개 메뉴 (회사 정보 섹션 내에서만 이동)
  '/about': {
    title: '회사 정보',
    items: [
      { path: '/about', label: '회사 소개', icon: <Building size={18} /> },
      { path: '/about/team', label: '팀 소개', icon: <Users size={18} /> },
      { path: '/about/vision', label: '비전 & 미션', icon: <Target size={18} /> },
      { path: '/about/history', label: '연혁', icon: <Clock size={18} /> },
      { path: '/about/contact', label: '오시는 길', icon: <MapPin size={18} /> }
    ]
  },
  
  // 시스템 가이드 메뉴 (시스템 안내 섹션 내에서만 이동)
  '/system-guide': {
    title: '시스템 안내',
    items: [
      { path: '/system-guide', label: '시작하기', icon: <BookMarked size={18} /> },
      { path: '/system-guide/settings', label: '환경설정', icon: <Settings size={18} /> },
      { path: '/system-guide/security', label: '보안설정', icon: <Shield size={18} /> },
      { path: '/system-guide/tech', label: '기술 사양', icon: <Cpu size={18} /> },
      { path: '/system-guide/api', label: 'API 문서', icon: <Code size={18} /> },
      { path: '/system-guide/troubleshoot', label: '문제해결', icon: <AlertCircle size={18} /> }
    ]
  }
};

function DynamicSidebar({
  onCategoryFilter, selectedCategory, onBrandFilter, selectedBrand,
  open = false, onClose,
}) {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // 현재 경로에 맞는 사이드바 메뉴 찾기
  const getMenuForPath = () => {
    // 홈페이지 체크 (정확히 홈 관련 경로만)
    if (currentPath === '/' || currentPath === '/Home' || currentPath === '/Shop') {
      return sidebarMenus['/'];
    }
    
    // ShowItem 관련 경로들 - 각각 고유한 메뉴 사용
    if (currentPath === '/ShowItem') {
      return sidebarMenus['/ShowItem'];
    }
    if (currentPath === '/ShowItem/new') {
      return sidebarMenus['/ShowItem/new'];
    }
    if (currentPath === '/ShowItem/best') {
      return sidebarMenus['/ShowItem/best'];
    }
    if (currentPath === '/ShowItem/sale') {
      return sidebarMenus['/ShowItem/sale'];
    }
    if (currentPath === '/ShowItem/gift') {
      return sidebarMenus['/ShowItem/gift'];
    }
    if (currentPath === '/ShowItem/fashion') {
      return sidebarMenus['/ShowItem/fashion'];
    }
    if (currentPath === '/ShowItem/electronics') {
      return sidebarMenus['/ShowItem/electronics'];
    }
    if (currentPath === '/ShowItem/living') {
      return sidebarMenus['/ShowItem/living'];
    }
    
    // quote-request 관련 경로들은 모두 quote-request 메뉴 사용
    if (currentPath === '/quote-request' || currentPath.startsWith('/quote-request/')) {
      return sidebarMenus['/quote-request'];
    }
    
    // delivery-tracking 관련 경로들은 모두 delivery-tracking 메뉴 사용
    if (currentPath === '/delivery-tracking' || currentPath.startsWith('/delivery-tracking/')) {
      return sidebarMenus['/delivery-tracking'];
    }
    
    // order-history와 order-detail은 order-history 메뉴 사용
    if (currentPath === '/order-history' || currentPath.startsWith('/order-detail')) {
      return sidebarMenus['/order-history'];
    }
    
    // mypage 관련 경로들은 mypage 메뉴 사용
    if (currentPath === '/mypage' || currentPath.startsWith('/mypage/') || currentPath === '/point-charge') {
      return sidebarMenus['/mypage'];
    }
    
    // support 관련 경로들은 모두 support 메뉴 사용
    if (currentPath === '/support' || currentPath.startsWith('/support/')) {
      return sidebarMenus['/support'];
    }
    
    // about 관련 경로들은 모두 about 메뉴 사용
    if (currentPath === '/about' || currentPath.startsWith('/about/')) {
      return sidebarMenus['/about'];
    }
    
    // system-guide 관련 경로들은 모두 system-guide 메뉴 사용
    if (currentPath === '/system-guide' || currentPath.startsWith('/system-guide/')) {
      return sidebarMenus['/system-guide'];
    }
    
    return null;
  };
  
  const currentMenu = getMenuForPath();
  
  if (!currentMenu) {
    return null; // 사이드바가 필요 없는 페이지
  }
  
  // 필터는 눌러도 화면 주소가 안 바뀐다. 그래서 서랍은 여기서 직접 닫는다 —
  // 안 닫으면 무엇으로 걸렀는지 보이지 않는다.
  const pick = (fn, value) => () => { fn && fn(value); onClose && onClose(); };

  return (
    <SidebarContainer open={open} aria-hidden={undefined}>
      <DrawerHead>
        <DrawerTitle>{currentMenu.title}</DrawerTitle>
        <DrawerClose type="button" onClick={onClose} aria-label="메뉴 닫기">
          <X size={20} />
        </DrawerClose>
      </DrawerHead>
      {currentMenu.sections ? (
        // 홈페이지: sections 기반 렌더링
        currentMenu.sections.map((section, sectionIndex) => (
          <div key={sectionIndex}>
            <SidebarTitle>{section.title}</SidebarTitle>
            <MenuList>
              {section.items.map((item, itemIndex) => (
                item.category ? (
                  <FilterButton
                    key={itemIndex}
                    onClick={pick(onCategoryFilter, item.category)}
                    active={selectedCategory === item.category}
                  >
                    <MenuIcon>{item.icon}</MenuIcon>
                    <MenuText>{item.label}</MenuText>
                    {item.badge && <Badge>{item.badge}</Badge>}
                  </FilterButton>
                ) : section.title === '브랜드' ? (
                  <FilterButton
                    key={itemIndex}
                    onClick={pick(onBrandFilter, item.label)}
                    active={selectedBrand === item.label}
                  >
                    <MenuIcon>{item.icon}</MenuIcon>
                    <MenuText>{item.label}</MenuText>
                    {item.badge && <Badge>{item.badge}</Badge>}
                  </FilterButton>
                ) : (
                  <MenuLink 
                    key={itemIndex}
                    to={item.path} 
                    active={currentPath === item.path ? 'true' : undefined}
                  >
                    <MenuIcon>{item.icon}</MenuIcon>
                    <MenuText>{item.label}</MenuText>
                    {item.badge && <Badge>{item.badge}</Badge>}
                  </MenuLink>
                )
              ))}
            </MenuList>
            {sectionIndex < currentMenu.sections.length - 1 && <Divider />}
          </div>
        ))
      ) : (
        // 다른 페이지: 기존 items 기반 렌더링
        <>
          <SidebarTitle>{currentMenu.title}</SidebarTitle>
          <MenuList>
            {currentMenu.items.map((item, index) => (
              <MenuLink 
                key={index}
                to={item.path} 
                active={currentPath === item.path ? 'true' : undefined}
              >
                <MenuIcon>{item.icon}</MenuIcon>
                <MenuText>{item.label}</MenuText>
                {item.badge && <Badge>{item.badge}</Badge>}
              </MenuLink>
            ))}
          </MenuList>
        </>
      )}
    </SidebarContainer>
  );
}

export default DynamicSidebar;